/**
 * LIVEL (2026-10-25): "Jums gali patikti" juosta krepšelio drawer'yje
 * (markup — snippets/livel-cart-upsell.liquid).
 *
 * - Prekės: Shopify /recommendations/products.json (intent=related) kiekvienai
 *   krepšelio prekei (iki 3), sumaišomos paeiliui, be jau esančių krepšelyje
 *   ir be neprieinamų.
 * - "Pridėti": tas pats kelias kaip product-form.js — /cart/add.js su
 *   cart-items-component sekcijomis + CartLinesUpdateEvent, todėl drawer'is,
 *   krepšelio ikona ir pristatymo blokas atsinaujina įprastai.
 * - Atsinaujina po kiekvieno cart:lines-update įvykio.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const MAX_ITEMS = 6;
const MAX_SOURCES = 3;

const root = () => window.Shopify?.routes?.root || '/';

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class LivelCartUpsell extends HTMLElement {
  #loadToken = 0;

  connectedCallback() {
    this.track = this.querySelector('.livel-upsell__track');
    if (!this.track) return;

    try {
      const promoData = this.querySelector('[data-livel-promo-1plus1]')?.textContent || '[]';
      this.promos = JSON.parse(promoData);
    } catch {
      this.promos = [];
    }
    this.promoIds = new Set(this.promos.map((promo) => promo.id));

    this.querySelectorAll('[data-dir]').forEach((button) => {
      button.addEventListener('click', () => this.#scrollBy(Number(button.dataset.dir)));
    });
    this.track.addEventListener('scroll', () => this.#syncArrows(), { passive: true });
    this.track.addEventListener('click', (event) => {
      const button = event.target.closest('.livel-upsell__add');
      if (button) this.#add(button);
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    event.promise?.then(() => this.#load()).catch(() => {});
  };

  async #load() {
    const token = ++this.#loadToken;
    try {
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      const inCart = new Set(cart.items.map((item) => item.product_id));
      const sources = [...inCart].slice(0, MAX_SOURCES);
      if (!sources.length) {
        this.hidden = true;
        return;
      }

      const lists = await Promise.all(
        sources.map((id) =>
          fetch(`${root()}recommendations/products.json?product_id=${id}&limit=10&intent=related`)
            .then((r) => (r.ok ? r.json() : { products: [] }))
            .then((data) => data.products || [])
            .catch(() => [])
        )
      );

      // 1+1 akcijos prekės — pirmos (net jei jų nėra rekomendacijose).
      const promoProducts = await Promise.all(
        this.promos
          .filter((promo) => !inCart.has(promo.id))
          .map((promo) =>
            fetch(`${root()}products/${encodeURIComponent(promo.handle)}.js`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
      );
      lists.unshift(promoProducts.filter(Boolean));

      const seen = new Set();
      const picks = [];
      for (const product of lists.shift()) {
        if (picks.length >= MAX_ITEMS || seen.has(product.id) || !product.available) continue;
        seen.add(product.id);
        picks.push(product);
      }
      for (let i = 0; i < 10 && picks.length < MAX_ITEMS; i++) {
        for (const list of lists) {
          const product = list[i];
          if (!product || seen.has(product.id) || inCart.has(product.id) || !product.available) continue;
          seen.add(product.id);
          picks.push(product);
          if (picks.length >= MAX_ITEMS) break;
        }
      }

      if (token !== this.#loadToken) return;
      if (!picks.length) {
        this.hidden = true;
        return;
      }

      this.track.innerHTML = picks.map((product) => this.#card(product)).join('');
      this.toggleAttribute('data-single', picks.length === 1);
      this.track.scrollLeft = 0;
      this.hidden = false;
      this.#syncArrows();
    } catch (error) {
      console.warn('[livel-cart-upsell]', error);
    }
  }

  #card(product) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const image = variant.featured_image?.src || product.featured_image;
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=160` : '';
    const showVariant = product.variants.length > 1 && variant.title && variant.title !== 'Default Title';
    const compare = variant.compare_at_price && variant.compare_at_price > variant.price;
    // 1+1: mygtukas iškart prideda 2 vnt. (antra nemokama per BXGY nuolaidą).
    const promo = this.promoIds.has(product.id);
    const quantity = promo ? 2 : 1;

    return `
      <li class="livel-upsell__card">
        <a class="livel-upsell__image" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
          ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="" width="60" height="60" loading="lazy">` : ''}
          ${promo ? '<span class="livel-upsell__badge">1+1</span>' : ''}
        </a>
        <div class="livel-upsell__info">
          <a class="livel-upsell__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <span class="livel-upsell__price">
            <span>${this.#money(variant.price)}</span>
            ${compare ? `<s>${this.#money(variant.compare_at_price)}</s>` : ''}
            ${showVariant ? `<span class="livel-upsell__variant">· ${escapeHtml(variant.title.split(' (')[0])}</span>` : ''}
          </span>
          ${promo ? '<span class="livel-upsell__promo">Antra nemokamai</span>' : ''}
        </div>
        <button
          type="button"
          class="livel-upsell__add"
          data-variant-id="${variant.id}"
          data-product-id="${product.id}"
          data-quantity="${quantity}"
          aria-label="${promo ? `Pridėti 2 vnt. (antra nemokamai): ${escapeHtml(product.title)}` : `Pridėti į krepšelį: ${escapeHtml(product.title)}`}"
        >+ Pridėti</button>
      </li>`;
  }

  #money(cents) {
    const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = Number(cents) / 100;
    const fmt = (decimals, thousands, decimal) => {
      const [whole, frac] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_with_comma_separator':
          return fmt(2, '.', ',');
        case 'amount_no_decimals':
          return fmt(0, ',', '');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(0, '.', '');
        case 'amount_with_space_separator':
          return fmt(2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return fmt(2, ' ', '.');
        default:
          return fmt(2, ',', '.');
      }
    });
  }

  #scrollBy(direction) {
    const card = this.track.querySelector('.livel-upsell__card');
    if (!card) return;
    const step = card.getBoundingClientRect().width + 10;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #syncArrows() {
    const [prev, next] = this.querySelectorAll('[data-dir]');
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (prev) prev.disabled = this.track.scrollLeft <= 2;
    if (next) next.disabled = this.track.scrollLeft >= maxScroll - 2;
  }

  async #add(button) {
    if (button.disabled) return;
    button.disabled = true;
    const label = button.textContent;
    button.textContent = '…';

    const variantId = Number(button.dataset.variantId);
    const quantity = Number(button.dataset.quantity) || 1;
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }], sections: sections.join(',') }),
      }).then((r) => r.json());

      if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'livel-cart-upsell',
          itemCount: quantity,
          productId: button.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      button.textContent = 'Nepavyko';
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 2000);
    }
  }
}

if (!customElements.get('livel-cart-upsell')) {
  customElements.define('livel-cart-upsell', LivelCartUpsell);
}
